import React, { FC, SVGProps } from "react";

/**
 * Line icons in the style of HubSpot's own set: a 32-unit grid, 2.28-unit
 * strokes with round ends, drawn in currentColor. They are our own drawings
 * (HubSpot's icon artwork is theirs), sized by the part that holds them.
 */
type IconProps = SVGProps<SVGSVGElement>;

const Line: FC<IconProps & { children: React.ReactNode }> = ({ children, style, ...rest }) => (
  <svg
    viewBox="0 0 32 32"
    aria-hidden="true"
    focusable="false"
    data-hs-icon=""
    fill="none"
    stroke="currentColor"
    strokeWidth={2.28}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ fill: "none", ...style }}
    {...rest}
  >
    {children}
  </svg>
);

export const HsSearchIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <circle cx="13.3" cy="13.3" r="10.3" />
    <path d="M20.6 20.6 28.2 28.2" />
  </Line>
);

export const HsLeftIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M21.3 5.9 11 16.3l10.3 10.3" />
  </Line>
);

export const HsRightIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M11 5.9 21.3 16.3 11 26.6" />
  </Line>
);

export const HsCloseIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M4 4l24 24M28 4 4 28" />
  </Line>
);

export const HsCheckIcon: FC<IconProps> = (p) => (
  <Line {...p} strokeWidth={3.6}>
    <path d="M6 16.8 12.7 23.4 26 9.6" />
  </Line>
);

export const HsDashIcon: FC<IconProps> = (p) => (
  <Line {...p} strokeWidth={3.6}>
    <path d="M7 16h18" />
  </Line>
);

export const HsExportIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M16.3 20.4V1.9M10.6 6.8l5.7-5.7 5.7 5.7" />
    <path d="M11.7 12.4H8.3c-1.3 0-2.3 1-2.3 2.3v13.7c0 1.3 1 2.3 2.3 2.3h16c1.3 0 2.3-1 2.3-2.3V14.7c0-1.3-1-2.3-2.3-2.3h-3.4" />
  </Line>
);

export const HsFilterIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M1.9 7h28.6M10.1 25.3h11.4M5.6 16.1h20.6" />
  </Line>
);

export const HsSettingsIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    {/* Eight square-topped teeth (tips at r 13.2, roots at r 10.4) around a 4-unit hub. */}
    <path d="M12.96 6.05 13.82 2.98h4.36l.86 3.07 1.84.77 2.79-1.57 3.08 3.08-1.57 2.79.77 1.84 3.07.86v4.36l-3.07.86-.77 1.84 1.57 2.79-3.08 3.08-2.79-1.57-1.84.77-.86 3.07h-4.36l-.86-3.07-1.84-.77-2.79 1.57-3.08-3.08 1.57-2.79-.77-1.84-3.07-.86v-4.36l3.07-.86.77-1.84-1.57-2.79 3.08-3.08 2.79 1.57z" />
    <circle cx="16" cy="16" r="4" />
  </Line>
);

export const HsExternalIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M13.3 19.5 27.9 4.9M18.9 4.9h9v9" />
    <path d="M23.3 19.3v8.6H5V9.6h8.6" />
  </Line>
);

export const HsCallIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M9.6 3.6 6 4.8C3.6 5.7 2.7 8.6 3.9 10.8a49 49 0 0 0 17.3 17.3c2.2 1.2 5.1.3 6-2.1l1.2-3.6-6.4-3.6-3.2 2.8a20 20 0 0 1-8.4-8.4l2.8-3.2z" />
    <path d="M19.4 4.6a8.3 8.3 0 0 1 8 8M19 9.2a3.7 3.7 0 0 1 3.8 3.8" />
  </Line>
);

export const HsEmailIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <rect x="1.9" y="6" width="28.2" height="20.6" rx="2.3" />
    <path d="M5.4 10.6 16 17.4l10.6-6.8" />
  </Line>
);

export const HsInfoIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <circle cx="16" cy="16" r="13.9" />
    <path d="M16 14.3v9.1M16 9.2v.2" />
  </Line>
);

export const HsDragIcon: FC<IconProps> = (p) => (
  <Line {...p} strokeWidth={3}>
    <path d="M12 6.5v.1M12 16v.1M12 25.5v.1M20 6.5v.1M20 16v.1M20 25.5v.1" />
  </Line>
);

export const HsListIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M10 8h18M10 16h18M10 24h18M4 8h.1M4 16h.1M4 24h.1" />
  </Line>
);

export const HsRefreshIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M27.4 13.7A11.6 11.6 0 0 0 6 9.8M4.6 18.3A11.6 11.6 0 0 0 26 22.2" />
    <path d="M5.4 3.4v6.9h6.9M26.6 28.6v-6.9h-6.9" />
  </Line>
);

export const HsSortDownIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M16.3 1.9v28.6M6 20.4l10.3 10.3 10.3-10.3" />
  </Line>
);

export const HsPhoneIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <rect x="8.6" y="2" width="14.8" height="28" rx="3.4" />
    <path d="M14.3 25.7h3.4" />
  </Line>
);

export const HsStarIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M16 2.9l4 8.6 9.3 1.1-6.9 6.4 1.8 9.2L16 23.6l-8.2 4.6 1.8-9.2-6.9-6.4 9.3-1.1z" />
  </Line>
);

export const HsBoltIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M18.3 2.3 6.9 18.3h9.1l-2.3 11.4L25.1 13.7H16z" />
  </Line>
);

export const HsEditIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M21.7 4.6l5.7 5.7L11.4 26.3l-7.3 1.6 1.6-7.3z" />
    <path d="M18.3 8l5.7 5.7" />
  </Line>
);

export const HsSlidersIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M2.3 8h6.8M15.9 8h13.8M2.3 24h13.7M22.9 24h6.8" />
    <circle cx="12.6" cy="8" r="3.4" />
    <circle cx="19.4" cy="24" r="3.4" />
  </Line>
);

export const HsChartIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M3.4 3.4v25.2h25.2" />
    <path d="M9.1 21.7v-5.7M16 21.7V9.1M22.9 21.7v-9.1" />
  </Line>
);

export const HsUsersIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <circle cx="12" cy="10.3" r="5.7" />
    <path d="M2.3 28.6c0-5.4 4.3-9.7 9.7-9.7s9.7 4.3 9.7 9.7" />
    <path d="M20.6 4.9a5.7 5.7 0 0 1 0 10.9M24 19.4c3.4 1.3 5.7 4.6 5.7 8.5" />
  </Line>
);

export const HsHelpIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <circle cx="16" cy="16" r="13.9" />
    <path d="M12 12.1a4.1 4.1 0 1 1 5.7 3.8c-1 .4-1.7 1.4-1.7 2.5v1.3M16 24.3v.2" />
  </Line>
);

export const HsPlusIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M16 3.4v25.2M3.4 16h25.2" />
  </Line>
);

export const HsSortUpIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M16.3 30.1V1.5M6 11.6 16.3 1.3l10.3 10.3" />
  </Line>
);

export const HsCalendarIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <rect x="2.9" y="5.1" width="26.2" height="24" rx="2.3" />
    <path d="M2.9 12.6h26.2M9.7 2v6.3M22.3 2v6.3" />
  </Line>
);

export const HsUndoIcon: FC<IconProps> = (p) => (
  <Line {...p}>
    <path d="M9.1 6.9 3.4 12.6l5.7 5.7" />
    <path d="M3.4 12.6h16a8.6 8.6 0 0 1 0 17.1h-6.8" />
  </Line>
);
